// Procedural models for everything in the teahouse.
import { GeoBuilder, m } from '../engine/geometry.js';
import { Mesh, Node } from '../engine/renderer.js';
import { col } from '../engine/math.js';
import { INGREDIENTS, SPECIES, RECIPES, SLUDGE } from './data.js';
import { modelMesh, modelParts, modelPart } from '../engine/assets.js';

let GL = null;
const cache = new Map();
export function initModels(gl) {
  GL = gl;
}
function cached(key, build) {
  let mesh = cache.get(key);
  if (!mesh) {
    const b = new GeoBuilder();
    build(b);
    mesh = new Mesh(GL, b);
    cache.set(key, mesh);
  }
  return mesh;
}
export function meshFrom(builder) {
  return new Mesh(GL, builder);
}
const N = (mesh, opts) => new Node(mesh, opts);

// ---------------------------------------------------------------- materials
export const MAT = {
  steel: m('#c9ced8', { rough: 0.28, metal: 1 }),
  darkSteel: m('#4a4f5c', { rough: 0.35, metal: 1 }),
  chrome: m('#e8ecf5', { rough: 0.12, metal: 1 }),
  gold: m('#ffcf7a', { rough: 0.25, metal: 1 }),
  copper: m('#e08a5a', { rough: 0.3, metal: 1 }),
  white: m('#f4f1fa', { rough: 0.45 }),
  ceramic: m('#fbf8ff', { rough: 0.18 }),
  black: m('#15141c', { rough: 0.5 }),
  rubber: m('#24222c', { rough: 0.8 }),
  glass: m('#cfefff', { rough: 0.05 }),
  wood: m('#a0714a', { rough: 0.6 }),
  darkWood: m('#5a3a28', { rough: 0.55 }),
};
const glowM = (c, e = 3) => m(c, { rough: 0.3, emit: e });

// ---------------------------------------------------------------- ingredients
function ingredientRaw(b, id) {
  const d = INGREDIENTS[id];
  const c = m(d.color, { rough: 0.45, emit: d.glow ? d.glow * 0.4 : 0 });
  const a = m(d.accent, { rough: 0.5, emit: d.glow ? d.glow * 0.3 : 0 });
  switch (d.shape) {
    case 'leaves':
    case 'leaves2':
      for (let i = 0; i < 5; i++) {
        const ang = (i / 5) * Math.PI * 2;
        b.leaf(0.13, 0.06, i % 2 ? c : a, { p: [Math.cos(ang) * 0.07, 0.05 + i * 0.012, Math.sin(ang) * 0.07], r: [0.3, ang, 0.2] });
      }
      if (d.shape === 'leaves2') b.sphere(0.05, a, { p: [0, 0.07, 0] });
      break;
    case 'mint':
      b.cylinder(0.012, 0.015, 0.2, m('#2a7a4a'), { p: [0, 0.1, 0] }, 6);
      for (let i = 0; i < 6; i++) {
        const ang = i * 2.1;
        b.leaf(0.1, 0.06, i % 2 ? c : a, { p: [Math.cos(ang) * 0.06, 0.06 + i * 0.025, Math.sin(ang) * 0.06], r: [0.6, ang, 0] });
      }
      break;
    case 'flower':
      for (let i = 0; i < 6; i++) {
        const ang = (i / 6) * Math.PI * 2;
        b.leaf(0.1, 0.055, c, { p: [Math.sin(ang) * 0.07, 0.08, Math.cos(ang) * 0.07], r: [0.35, ang, 0] });
      }
      b.sphere(0.04, glowM(d.accent, 2.5), { p: [0, 0.1, 0] });
      b.cylinder(0.01, 0.012, 0.08, m('#3a7a4a'), { p: [0, 0.04, 0] }, 6);
      break;
    case 'root':
      b.capsule(0.045, 0.2, c, { p: [0, 0.06, 0], r: [0, 0.4, Math.PI / 2 - 0.15] });
      b.capsule(0.03, 0.12, a, { p: [0.08, 0.05, 0.05], r: [0.4, 0.6, Math.PI / 2 + 0.4] });
      break;
    case 'sticks':
      for (let i = 0; i < 3; i++) b.cylinder(0.03, 0.03, 0.28, i === 1 ? a : c, { p: [(i - 1) * 0.055, 0.04 + (i === 1 ? 0.04 : 0), 0], r: [Math.PI / 2, 0.1 * i, 0] }, 8);
      break;
    case 'pod':
      b.sphere(0.12, c, { p: [0, 0.1, 0], s: [0.75, 1.25, 0.75] });
      for (let i = 0; i < 6; i++) b.cylinder(0.012, 0.012, 0.26, a, { p: [Math.cos(i) * 0.09, 0.1, Math.sin(i) * 0.09] }, 4);
      break;
    case 'jelly':
      b.sphere(0.12, m(d.color, { rough: 0.1, emit: 2.2 }), { p: [0, 0.09, 0], s: [1, 0.8, 1] });
      b.sphere(0.05, m('#ffffff', { rough: 0.1, emit: 4 }), { p: [0.02, 0.12, 0.02] });
      break;
    case 'cube':
      b.roundBox(0.18, 0.16, 0.18, 0.03, m(d.color, { rough: 0.15, emit: 0.3 }), { p: [0, 0.08, 0], r: [0, 0.4, 0] });
      b.roundBox(0.1, 0.09, 0.1, 0.02, m('#ffffff', { rough: 0.1, emit: 0.4 }), { p: [0.1, 0.05, 0.08], r: [0, 1, 0] });
      break;
    case 'jar':
      b.cylinder(0.1, 0.1, 0.18, c, { p: [0, 0.09, 0] }, 14);
      b.cylinder(0.105, 0.105, 0.05, a, { p: [0, 0.2, 0] }, 14);
      b.roundBox(0.12, 0.06, 0.005, 0.002, m('#7a4a2a'), { p: [0, 0.09, 0.1] });
      break;
    case 'crystals':
      for (let i = 0; i < 5; i++) b.cone(0.045, 0.2 - i * 0.02, m(i % 2 ? d.color : d.accent, { rough: 0.1, emit: 0.8 }), { p: [Math.cos(i * 1.3) * 0.05, 0.09, Math.sin(i * 1.3) * 0.05], r: [Math.cos(i) * 0.4, 0, Math.sin(i) * 0.4] }, 5);
      break;
    case 'coconut':
      b.sphere(0.13, c, { p: [0, 0.12, 0] }, 12, 10);
      for (let i = 0; i < 3; i++) b.sphere(0.018, MAT.black, { p: [Math.cos(i * 2.1) * 0.04, 0.245, Math.sin(i * 2.1) * 0.04] }, 6, 4);
      break;
    case 'aloe':
      for (let i = 0; i < 5; i++) b.cone(0.045, 0.3, i % 2 ? c : a, { p: [Math.cos(i * 1.25) * 0.04, 0.14, Math.sin(i * 1.25) * 0.04], r: [Math.cos(i * 1.25) * 0.35, 0, Math.sin(i * 1.25) * 0.35], s: [1, 1, 0.45] }, 6);
      break;
    case 'mango':
      b.sphere(0.13, c, { p: [0, 0.11, 0], s: [1.1, 0.85, 0.85], r: [0, 0, 0.3] });
      b.sphere(0.07, m(d.accent), { p: [0.07, 0.13, 0.04] });
      b.leaf(0.08, 0.04, m('#3aa35a'), { p: [-0.05, 0.22, 0], r: [0.3, 1, 0.5] });
      break;
    case 'banana':
      bananaBunch(b, d);
      break;
    case 'pineapple':
      b.sphere(0.12, c, { p: [0, 0.13, 0], s: [0.9, 1.25, 0.9] }, 10, 8);
      for (let i = 0; i < 6; i++) b.leaf(0.13, 0.04, m(d.accent), { p: [Math.cos(i) * 0.03, 0.32, Math.sin(i) * 0.03], r: [-1.1, i * 1.05, 0] });
      break;
    case 'orb':
      b.sphere(0.12, c, { p: [0, 0.12, 0] }, 12, 10);
      b.sphere(0.06, m(d.accent, { emit: 0.4 }), { p: [0.06, 0.16, 0.06] });
      break;
    case 'berries':
      for (let i = 0; i < 7; i++) b.sphere(0.05, i % 3 ? c : m(d.accent), { p: [Math.cos(i * 0.9) * 0.08 * (i % 2 ? 1 : 0.4), 0.05 + (i > 4 ? 0.07 : 0), Math.sin(i * 0.9) * 0.08], s: [1, 1.3, 1] }, 8, 6);
      break;
    case 'apple':
      b.sphere(0.13, c, { p: [0, 0.13, 0], s: [1, 0.92, 1] }, 14, 10);
      b.cylinder(0.01, 0.012, 0.08, MAT.darkWood, { p: [0, 0.27, 0] }, 5);
      b.leaf(0.07, 0.035, m(d.accent), { p: [0.04, 0.28, 0], r: [0.4, 0, -0.5] });
      break;
    case 'pearls': {
      // a little glass dish heaped with glossy tapioca pearls
      b.lathe([[0.001, 0], [0.1, 0], [0.13, 0.05], [0.12, 0.05], [0.09, 0.012], [0.001, 0.012]], 16, m('#e8f4ff', { rough: 0.08 }));
      const pm = pearlMat();
      for (let i = 0; i < 14; i++) {
        const k = i < 8 ? 0 : i < 13 ? 1 : 2;
        const a = i * 2.4, r = [0.075, 0.042, 0][k];
        b.sphere(0.026, pm, { p: [Math.cos(a) * r, 0.034 + k * 0.03, Math.sin(a) * r] }, 8, 6);
      }
      break;
    }
    case 'cloud':
      b.sphere(0.09, m(d.color, { rough: 0.2, emit: 0.5 }), { p: [0, 0.1, 0] });
      b.sphere(0.07, m(d.color, { rough: 0.2, emit: 0.5 }), { p: [0.08, 0.08, 0.02] });
      b.sphere(0.065, m(d.accent, { rough: 0.2, emit: 0.6 }), { p: [-0.08, 0.08, -0.01] });
      b.sphere(0.06, m(d.color, { rough: 0.2, emit: 0.5 }), { p: [0.01, 0.15, 0.05] });
      break;
  }
}
function bananaBunch(b, d) {
  const peel = col(d.color), stem = col('#9aa84a'), tip = col('#5a4024'), pale = col('#fff2a0');
  const spread = [-0.42, 0.02, 0.42];
  spread.forEach((ang, k) => {
    const pts = [], rad = [], cols = [];
    const lift = k === 1 ? 0.035 : 0;
    for (let i = 0; i <= 14; i++) {
      const t = i / 14;
      const along = 0.02 + t * 0.29;
      const x = -0.14 + Math.cos(ang * (0.3 + t * 0.7)) * along;
      const z = Math.sin(ang * (0.3 + t * 0.7)) * along;
      const y = 0.045 + lift + 0.13 * (t - 0.42) * (t - 0.42) - 0.012;
      pts.push([x, y, z]);
      rad.push(t < 0.1 ? 0.013 + t * 0.2 : t > 0.86 ? 0.036 * Math.max(0.3, (1 - t) / 0.14) : 0.036 + Math.sin(t * Math.PI) * 0.004);
      const c = t < 0.1 ? stem : t > 0.96 ? tip : t < 0.2 ? stem.map((v, j) => v + (peel[j] - v) * ((t - 0.1) / 0.1)) : t > 0.4 && t < 0.6 ? peel.map((v, j) => v * 0.94 + pale[j] * 0.06) : peel;
      cols.push(c);
    }
    b.tube(pts, rad, m(d.color, { rough: 0.42 }), 6, cols, 0.9);
  });
  // crown where the stems meet
  b.cylinder(0.02, 0.026, 0.035, m('#7a8a30', { rough: 0.6 }), { p: [-0.145, 0.058, 0], r: [0, 0, Math.PI / 2 - 0.5] }, 8);
  b.sphere(0.017, m('#4a3a1c', { rough: 0.7 }), { p: [-0.16, 0.07, 0] }, 8, 6);
}
function ingredientChopped(b, id) {
  const d = INGREDIENTS[id];
  if (id === 'banana') {
    for (let i = 0; i < 7; i++) {
      const ang = i * 2.4, r = 0.02 + (i % 3) * 0.04;
      const t = { p: [Math.cos(ang) * r, 0.012 + (i > 4 ? 0.022 : 0), Math.sin(ang) * r], r: [0.25 * (i % 3), ang, 0.2 * ((i + 1) % 2)] };
      b.cylinder(0.038, 0.038, 0.022, m('#fff4c6', { rough: 0.5 }), t, 14);
      b.torus(0.038, 0.004, m(d.color, { rough: 0.45 }), t, 14, 4);
      b.disc(0.009, m('#c8a860', { rough: 0.8 }), { p: [t.p[0], t.p[1] + 0.0115, t.p[2]], r: t.r }, 8);
    }
    return;
  }
  const c = m(d.color, { rough: 0.4, emit: d.glow ? d.glow * 0.4 : 0 });
  const a = m(d.accent, { rough: 0.4 });
  for (let i = 0; i < 7; i++) {
    const ang = i * 2.4, r = 0.03 + (i % 3) * 0.035;
    b.roundBox(0.075, 0.05, 0.075, 0.015, i % 3 === 0 ? a : c, { p: [Math.cos(ang) * r, 0.025 + (i > 4 ? 0.04 : 0), Math.sin(ang) * r], r: [0.2 * i, ang, 0.1 * i] }, 2);
  }
}
function ingredientGround(b, id) {
  const d = INGREDIENTS[id];
  b.lathe([[0.001, 0], [0.12, 0.005], [0.13, 0.03], [0.115, 0.04], [0.001, 0.04]], 12, MAT.ceramic, { p: [0, 0, 0] });
  b.sphere(0.1, m(d.color, { rough: 0.9 }), { p: [0, 0.04, 0], s: [1, 0.55, 1] }, 12, 8);
  b.sphere(0.04, m(d.accent, { rough: 0.9 }), { p: [0.03, 0.08, 0.02], s: [1, 0.6, 1] }, 8, 6);
}
export function ingredientMesh(id, state = 'raw') {
  // Blender-built raw ingredients (tools/blender/build_ingredients.py) replace the code-built shapes
  const a = state === 'raw' && modelMesh('ing_' + id);
  if (a) return a;
  return cached('ing:' + id + ':' + state, (b) => {
    if (state === 'chopped') ingredientChopped(b, id);
    else if (state === 'ground') ingredientGround(b, id);
    else ingredientRaw(b, id);
  });
}

// ---------------------------------------------------------------- cups & appliances
export function cupMesh(dirty = false) {
  const a = modelMesh(dirty ? 'cup_dirty' : 'cup');
  if (a) return a;
  return cached('cup:' + dirty, (b) => {
    const cm = dirty ? m('#d8cbb8', { rough: 0.5 }) : MAT.ceramic;
    b.lathe([[0.001, 0], [0.07, 0], [0.09, 0.02], [0.12, 0.16], [0.125, 0.17], [0.113, 0.17], [0.105, 0.03], [0.001, 0.03]], 18, cm);
    b.torus(0.045, 0.012, cm, { p: [0.13, 0.095, 0], r: [Math.PI / 2, 0, 0] }, 12, 6, Math.PI * 2);
    b.torus(0.12, 0.006, dirty ? m('#9a8a6a') : MAT.gold, { p: [0, 0.165, 0] }, 20, 4);
    if (dirty) b.disc(0.1, m('#6a5a3a', { rough: 0.9 }), { p: [0, 0.04, 0] }, 12);
  });
}
export function liquidMesh() {
  return cached('liquid', (b) => b.disc(0.108, m('#ffffff', { rough: 0.05 }), { p: [0, 0, 0] }, 18));
}

// ---------------------------------------------------------------- drinks
// Every drink gets its own look: liquid colour + a garnish per signature ingredient,
// smoothie dome and striped straw for blender drinks, foam art for creamy teas,
// bubbles for fizzy drinks and a glowing star pick for luminous ones.
const SURF = 0.14; // liquid height inside the cup (cup body space)
const RIM = 0.118;
const ang2 = (a, r, y) => [Math.cos(a) * r, y, Math.sin(a) * r];
function mix(a, b, t) {
  const A = col(a), B = col(b);
  return A.map((v, i) => v + (B[i] - v) * t);
}
const STRAWS = ['#ff5aa8', '#4ad8ff', '#ffd23f', '#9b6bff', '#58e07a', '#ff7a3d'];
function hashStr(s) {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}
function stick(b, from, to, r, mat, sides = 8) {
  b.tube([from, to], r, mat, sides);
}
/** Garnish for one ingredient. slot: 0 = primary (rim, front-right), 1 = secondary (top, back-left). */
function garnish(b, id, slot, topY, blender) {
  const d = INGREDIENTS[id];
  const c = m(d.color, { rough: 0.4, emit: d.glow ? d.glow * 0.3 : 0 });
  const a = m(d.accent, { rough: 0.45 });
  const rimA = slot === 0 ? -0.6 : 2.3; // radians around the cup (0 = +x / handle side)
  const topP = slot === 0 ? [0.025, topY, 0.03] : [-0.035, topY, -0.02];
  switch (id) {
    case 'mango': {
      // chunky wedge perched on the rim
      const p = ang2(rimA, RIM, 0.185);
      b.roundBox(0.075, 0.06, 0.04, 0.015, c, { p, r: [0, -rimA, 0.35] }, 2);
      b.roundBox(0.078, 0.014, 0.043, 0.006, a, { p: [p[0], p[1] - 0.034, p[2]], r: [0, -rimA, 0.35] }, 1);
      break;
    }
    case 'pineapple': {
      const p = ang2(rimA, RIM, 0.18);
      b.cone(0.05, 0.09, c, { p, r: [Math.PI, -rimA, Math.PI / 2] }, 3);
      for (let i = 0; i < 3; i++) b.leaf(0.06, 0.018, m(d.accent), { p: [p[0], p[1] + 0.05, p[2]], r: [-0.4 + i * 0.4, -rimA + i, 0.3] });
      break;
    }
    case 'apple': {
      const p = ang2(rimA, RIM, 0.18);
      const t = { p, r: [Math.PI / 2, 0, -rimA + Math.PI / 2] };
      b.cylinder(0.05, 0.05, 0.014, m('#fff3dc', { rough: 0.5 }), t, 16);
      b.torus(0.05, 0.007, c, t, 16, 5);
      break;
    }
    case 'passion': {
      const p = ang2(rimA, RIM - 0.01, 0.172);
      b.hemisphere(0.045, c, { p, r: [Math.PI, 0, 0.3] }, 12, 6);
      b.disc(0.04, m(d.accent, { rough: 0.3, emit: 0.3 }), { p: [p[0], p[1] + 0.001, p[2]], r: [0, 0, 0.3] }, 12);
      for (let i = 0; i < 5; i++) b.sphere(0.006, MAT.black, { p: [p[0] + Math.cos(i * 1.3) * 0.02, p[1] + 0.004, p[2] + Math.sin(i * 1.3) * 0.02] }, 5, 3);
      break;
    }
    case 'banana':
      for (let i = 0; i < 3; i++) {
        const q = [topP[0] + (i - 1) * 0.04, topY + 0.006 + (i === 1 ? 0.008 : 0), topP[2] + (i % 2) * 0.03];
        b.cylinder(0.026, 0.026, 0.014, m('#fff4c6', { rough: 0.5 }), { p: q, r: [0.2 * (i - 1), 0, 0.25] }, 12);
        b.torus(0.026, 0.004, m(d.color), { p: q, r: [0.2 * (i - 1), 0, 0.25] }, 12, 4);
      }
      break;
    case 'coconut':
      for (let i = 0; i < 7; i++) {
        const q = [topP[0] + Math.cos(i * 2.2) * 0.04, topY + 0.006 + (i % 2) * 0.006, topP[2] + Math.sin(i * 2.2) * 0.04];
        b.roundBox(0.03, 0.008, 0.014, 0.003, m('#fffaf0', { rough: 0.6 }), { p: q, r: [0.3 * (i % 3), i, 0.2] }, 1);
      }
      break;
    case 'aloe':
      for (let i = 0; i < 4; i++) {
        const q = [topP[0] + Math.cos(i * 1.7) * 0.035, topY + 0.01, topP[2] + Math.sin(i * 1.7) * 0.035];
        b.roundBox(0.026, 0.024, 0.026, 0.006, m('#c8ffd0', { rough: 0.08, emit: 0.25 }), { p: q, r: [0.3, i, 0.2] }, 1);
      }
      break;
    case 'mint': {
      const q = [topP[0], topY + 0.012, topP[2]];
      for (let i = 0; i < 3; i++) b.leaf(0.045, 0.028, i === 1 ? c : m(d.color, { rough: 0.4 }), { p: [q[0] + Math.cos(i * 2.1) * 0.022, q[1] + i * 0.006, q[2] + Math.sin(i * 2.1) * 0.022], r: [0.35, i * 2.1, 0] });
      break;
    }
    case 'jasmine': {
      const q = [topP[0], topY + 0.008, topP[2]];
      for (let i = 0; i < 5; i++) b.leaf(0.035, 0.022, m('#fbf8ff', { rough: 0.3, emit: 0.15 }), { p: [q[0] + Math.cos(i * 1.2566) * 0.028, q[1], q[2] + Math.sin(i * 1.2566) * 0.028], r: [0.25, Math.PI / 2 - i * 1.2566, 0] });
      b.sphere(0.013, m(d.accent, { emit: 2 }), { p: [q[0], q[1] + 0.006, q[2]] }, 8, 6);
      break;
    }
    case 'tea':
    case 'matcha':
      for (let i = 0; i < 2; i++) b.leaf(0.05, 0.022, c, { p: [topP[0] + i * 0.03, topY + 0.004, topP[2] - i * 0.02], r: [0.05, i * 1.4 + 0.3, 0] });
      break;
    case 'licorice':
    case 'cinnamon': {
      const base = ang2(rimA + Math.PI, 0.04, SURF - 0.02);
      const tip = ang2(rimA, 0.2, 0.3);
      stick(b, base, tip, id === 'cinnamon' ? 0.014 : 0.011, id === 'cinnamon' ? m('#b0643a', { rough: 0.7 }) : m('#3a2418', { rough: 0.5 }), 7);
      if (id === 'cinnamon') for (let i = 0; i < 9; i++) b.sphere(0.005, m('#8a4a24', { rough: 0.9 }), { p: [Math.cos(i * 2.4) * 0.05 * ((i % 3) / 3 + 0.3), topY + 0.002, Math.sin(i * 2.4) * 0.05 * ((i % 3) / 3 + 0.3)] }, 4, 3);
      break;
    }
    case 'cacao': {
      b.torus(0.022, 0.008, m('#4a2414', { rough: 0.4 }), { p: [topP[0], topY + 0.012, topP[2]], r: [1.2, 0.3, 0] }, 12, 5, Math.PI * 1.6);
      for (let i = 0; i < 12; i++) b.sphere(0.004, m('#3a1c10', { rough: 0.9 }), { p: [Math.cos(i * 2.4) * 0.07 * ((i % 4) / 4 + 0.2), topY + 0.002, Math.sin(i * 2.4) * 0.07 * ((i % 4) / 4 + 0.2)] }, 4, 3);
      break;
    }
    case 'glow': {
      // glowing star on a pick
      const base = ang2(rimA + Math.PI, 0.03, SURF);
      const top = ang2(rimA, 0.07, 0.3);
      stick(b, base, top, 0.004, MAT.gold, 5);
      const gm = m(d.color, { rough: 0.2, emit: 3.2 });
      b.sphere(0.018, gm, { p: top }, 8, 6);
      for (let i = 0; i < 5; i++) {
        const t = (i / 5) * Math.PI * 2;
        b.cone(0.012, 0.03, gm, { p: [top[0] + Math.sin(t) * 0.02, top[1] + Math.cos(t) * 0.02, top[2]], r: [0, 0, -t] }, 4);
      }
      break;
    }
    case 'dryice':
      for (let i = 0; i < 5; i++) b.sphere(0.03 - i * 0.003, m('#eef8ff', { rough: 0.2, emit: 0.35 }), { p: [Math.cos(i * 1.9) * 0.05, topY + 0.02 + i * 0.012, Math.sin(i * 1.9) * 0.05], s: [1.3, 0.6, 1.3] }, 10, 6);
      break;
    case 'salt':
      for (let i = 0; i < 22; i++) {
        const t = (i / 22) * Math.PI * 2;
        b.cone(0.009, 0.018, m(i % 3 ? '#ffffff' : d.color, { rough: 0.1, emit: 0.6 }), { p: ang2(t, 0.122, 0.176), r: [0.3 * Math.sin(i), 0, 0.3 * Math.cos(i)] }, 4);
      }
      break;
    case 'goji': {
      const base = ang2(rimA + Math.PI, 0.02, SURF);
      const top = ang2(rimA, 0.1, 0.27);
      stick(b, base, top, 0.004, MAT.wood, 5);
      for (let i = 0; i < 3; i++) {
        const t = 0.45 + i * 0.2;
        b.sphere(0.017, c, { p: base.map((v, k) => v + (top[k] - v) * t), s: [1, 1.3, 1] }, 8, 6);
      }
      break;
    }
    case 'nutcream':
      if (blender) {
        // whipped cream swirl
        const cm = m('#fff8ee', { rough: 0.55 });
        for (let i = 0; i < 3; i++) b.torus(0.05 - i * 0.014, 0.018 - i * 0.003, cm, { p: [0, topY + 0.012 + i * 0.018, 0] }, 16, 6);
        b.cone(0.016, 0.04, cm, { p: [0, topY + 0.07, 0] }, 8);
      }
      break;
  }
}
const TOPPERS = new Set(['banana', 'coconut', 'aloe', 'mint', 'jasmine', 'tea', 'matcha', 'cacao', 'dryice']);
export function drinkMesh(id, fizzy = false) {
  return cached('drink:' + id + (fizzy ? ':fizz' : ''), (b) => {
    const r = RECIPES[id] || SLUDGE;
    const glow = r.glow || 0;
    const liq = m(r.color, { rough: 0.12, emit: glow * 0.9 + 0.05 });
    if (!RECIPES[id]) {
      // murky sludge: lumpy, dull, with a sad bubble
      b.disc(0.108, liq, { p: [0, SURF, 0] }, 18);
      for (let i = 0; i < 6; i++) b.sphere(0.022, m('#3a3a2a', { rough: 0.9 }), { p: [Math.cos(i * 2.3) * 0.055, SURF + 0.002, Math.sin(i * 2.3) * 0.055], s: [1, 0.45, 1] }, 8, 4);
      b.sphere(0.02, m('#6a6a4a', { rough: 0.2 }), { p: [0.03, SURF + 0.012, -0.03] }, 8, 6);
      return;
    }
    const blender = r.app === 'blender';
    const ings = [...new Set(r.ing.filter((x) => x !== 'water'))];
    const creamy = ings.includes('nutcream');
    let topY = SURF;
    if (blender) {
      // thick smoothie dome with a lighter swirl band
      b.sphere(0.108, liq, { p: [0, SURF - 0.004, 0], s: [1, 0.3, 1] }, 20, 8);
      topY = SURF + 0.028;
      const sec = ings.find((x) => x !== 'nutcream' && INGREDIENTS[x].color.toLowerCase() !== r.color.toLowerCase());
      const band = sec ? mix(r.color, INGREDIENTS[sec].color, 0.55) : mix(r.color, '#ffffff', 0.35);
      b.torus(0.07, 0.009, m(band, { rough: 0.2, emit: glow * 0.6 }), { p: [0, SURF + 0.018, 0], s: [1, 0.5, 1] }, 20, 5);
    } else {
      b.disc(0.108, liq, { p: [0, SURF, 0] }, 20);
      if (creamy || ings.includes('matcha')) {
        // latte art: foam ring + heart
        const foam = m(mix(r.color, '#fff8ee', 0.75), { rough: 0.4 });
        b.torus(0.085, 0.012, foam, { p: [0, SURF + 0.001, 0], s: [1, 0.25, 1] }, 20, 4);
        b.sphere(0.022, foam, { p: [-0.013, SURF + 0.001, 0.008], s: [1, 0.15, 1] }, 10, 4);
        b.sphere(0.022, foam, { p: [0.013, SURF + 0.001, 0.008], s: [1, 0.15, 1] }, 10, 4);
        b.cone(0.03, 0.045, foam, { p: [0, SURF + 0.001, -0.018], r: [Math.PI / 2, 0, 0], s: [1, 1, 0.15] }, 10);
      } else {
        // plain hot tea: a thin darker ring where the tea meets the cup
        b.torus(0.104, 0.004, m(mix(r.color, '#000000', 0.35), { rough: 0.2 }), { p: [0, SURF + 0.001, 0] }, 24, 4);
      }
    }
    // garnishes: first a rim/stick garnish, then a topper, max two
    const rimFirst = ings.filter((x) => !TOPPERS.has(x) && x !== 'nutcream');
    const tops = ings.filter((x) => TOPPERS.has(x));
    const picks = [];
    if (rimFirst[0]) picks.push(rimFirst[0]);
    if (tops[0]) picks.push(tops[0]);
    else if (rimFirst[1]) picks.push(rimFirst[1]);
    if (!picks.length && ings[0] && ings[0] !== 'nutcream') picks.push(ings[0]);
    if (creamy && blender) garnish(b, 'nutcream', 1, topY, blender);
    picks.forEach((g, i) => garnish(b, g, i, creamy && blender && TOPPERS.has(g) ? topY + 0.05 : topY, blender));
    if (blender) {
      // striped bendy straw
      const sc = STRAWS[hashStr(id) % STRAWS.length];
      const pts = [], cols = [];
      const A = 2.45, B2 = 2.2;
      const P0 = ang2(A, 0.03, SURF - 0.02), P1 = ang2(A, 0.075, 0.275), P2 = ang2(B2, 0.125, 0.305);
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        const q = t < 0.8 ? P0.map((v, k) => v + (P1[k] - v) * (t / 0.8)) : P1.map((v, k) => v + (P2[k] - v) * ((t - 0.8) / 0.2));
        pts.push(q);
        cols.push(col(i % 2 ? '#ffffff' : sc));
      }
      b.tube(pts, 0.009, m(sc, { rough: 0.35 }), 8, cols);
    }
    if (fizzy) {
      const bm = m('#e8fdff', { rough: 0.05, emit: 0.3 });
      for (let i = 0; i < 8; i++) b.sphere(0.008 + (i % 3) * 0.004, bm, { p: [Math.cos(i * 2.2) * 0.075, topY - 0.004 + (i % 2) * 0.004, Math.sin(i * 2.2) * 0.075] }, 8, 5);
      for (let i = 0; i < 3; i++) b.sphere(0.007, bm, { p: [Math.cos(i * 2) * 0.03, topY + 0.04 + i * 0.03, Math.sin(i * 2) * 0.03] }, 6, 4);
    }
  });
}
export function kettleMesh() {
  const a = modelMesh('kettle');
  if (a) return a;
  return cached('kettle', (b) => {
    const enamel = m('#f0604e', { rough: 0.22 });
    b.lathe([[0.001, 0], [0.16, 0], [0.2, 0.06], [0.21, 0.14], [0.18, 0.24], [0.1, 0.3], [0.001, 0.31]], 20, enamel);
    b.lathe([[0.1, 0.295], [0.11, 0.31], [0.001, 0.33]], 16, m('#fff2e0', { rough: 0.3 }));
    b.cylinder(0.02, 0.035, 0.22, enamel, { p: [0.22, 0.2, 0], r: [0, 0, -0.9] }, 8);
    b.torus(0.205, 0.012, MAT.gold, { p: [0, 0.14, 0] }, 20, 5);
    b.torus(0.12, 0.018, MAT.black, { p: [0, 0.32, 0], r: [0, 0, Math.PI / 2] }, 14, 6, Math.PI);
    b.sphere(0.035, glowM('#ffb86b', 2), { p: [0, 0.32, 0] });
    b.torus(0.2, 0.012, MAT.copper, { p: [0, 0.09, 0] }, 20, 5);
  });
}
export function jugMesh() {
  const a = modelMesh('jug', 'base');
  if (a) return a;
  return cached('jug', (b) => {
    b.cylinder(0.14, 0.12, 0.05, MAT.darkSteel, { p: [0, 0.025, 0] }, 16);
    b.cylinder(0.02, 0.02, 0.06, MAT.chrome, { p: [0, 0.07, 0] }, 6);
    b.box(0.18, 0.01, 0.03, MAT.chrome, { p: [0, 0.08, 0], r: [0, 0.5, 0] });
    b.box(0.18, 0.01, 0.03, MAT.chrome, { p: [0, 0.085, 0], r: [0, -0.7, 0] });
    b.cylinder(0.15, 0.15, 0.04, MAT.black, { p: [0, 0.44, 0] }, 16);
    b.torus(0.07, 0.015, MAT.black, { p: [0.16, 0.25, 0], r: [Math.PI / 2, 0, Math.PI / 2] }, 10, 5, Math.PI);
  });
}
export function jugGlassMesh() {
  const a = modelMesh('jug', 'FX_glass');
  if (a) return a;
  return cached('jugglass', (b) => b.lathe([[0.12, 0.05], [0.15, 0.42]], 18, m('#bfefff'), {}));
}
export function jugLiquidMesh() {
  return cached('jugliquid', (b) => b.cylinder(0.13, 0.115, 1, m('#ffffff', { rough: 0.1 }), { p: [0, 0.5, 0] }, 16));
}
export function extinguisherMesh() {
  const a = modelMesh('extinguisher');
  if (a) return a;
  return cached('ext', (b) => {
    b.capsule(0.09, 0.22, m('#3a8aff', { rough: 0.3, metal: 0.4 }), { p: [0, 0.2, 0] });
    b.cylinder(0.04, 0.05, 0.06, MAT.chrome, { p: [0, 0.37, 0] }, 8);
    b.box(0.16, 0.03, 0.04, MAT.black, { p: [0.05, 0.4, 0] });
    b.cylinder(0.015, 0.02, 0.12, MAT.black, { p: [0, 0.36, 0.08], r: [1.2, 0, 0] }, 6);
    b.torus(0.092, 0.012, glowM('#aee8ff', 2.5), { p: [0, 0.26, 0] }, 16, 4);
  });
}

// ---------------------------------------------------------------- boba
// A tall clear boba cup. The see-through shell is its own mesh (drawn translucent in game),
// so the pearls against the wall and the tea above them show through it.
export const BOBA_H = 0.3;
const BOBA_R0 = 0.082, BOBA_R1 = 0.108;
const bobaR = (y) => BOBA_R0 + ((BOBA_R1 - BOBA_R0) * y) / BOBA_H;
function pearlMat() {
  return m('#2e1820', { rough: 0.12 });
}
const STRAW_C = '#ff5aa8';
/** Fat boba straw with candy stripes. */
function strawTube(b, from, to) {
  const pts = [], cols = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    pts.push(from.map((v, k) => v + (to[k] - v) * t));
    cols.push(col(i % 2 ? '#ffffff' : STRAW_C));
  }
  b.tube(pts, 0.015, m(STRAW_C, { rough: 0.35 }), 10, cols);
}
export function bobaShellMesh() {
  return cached('boba:shell', (b) => {
    b.lathe([[0.001, 0.004], [BOBA_R0 - 0.004, 0.004], [BOBA_R0, 0.012], [BOBA_R1, BOBA_H], [BOBA_R1 - 0.005, BOBA_H], [BOBA_R0 - 0.005, 0.014], [0.001, 0.014]], 24, m('#eaf6ff', { rough: 0.06 }));
  });
}
/** The opaque bits of the boba cup: rim lip and base ring. */
export function bobaCupMesh() {
  return cached('boba:cup', (b) => {
    const lip = m('#f6fbff', { rough: 0.2 });
    b.torus(BOBA_R1, 0.006, lip, { p: [0, BOBA_H, 0] }, 28, 5);
    b.torus(BOBA_R0 - 0.002, 0.005, lip, { p: [0, 0.008, 0] }, 24, 4);
  });
}
export function bobaPearlsMesh() {
  return cached('boba:pearls', (b) => {
    const pm = pearlMat();
    // rings of pearls pressed against the wall, where they show through the cup
    for (let k = 0; k < 3; k++) {
      const y = 0.03 + k * 0.026, rr = bobaR(y) - 0.021;
      const n = Math.floor((Math.PI * 2 * rr) / 0.034);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + k * 0.5;
        b.sphere(0.017, pm, { p: [Math.cos(a) * rr, y, Math.sin(a) * rr] }, 8, 6);
      }
    }
    for (let i = 0; i < 5; i++) b.sphere(0.017, pm, { p: [Math.cos(i * 1.26) * 0.03, 0.03 + (i % 2) * 0.02, Math.sin(i * 1.26) * 0.03] }, 8, 6);
  });
}
/** Tea column (white, tinted per drink). Narrower at the bottom so the pearls poke out of it. */
export function bobaLiquidMesh() {
  return cached('boba:liquid', (b) => {
    const y0 = 0.016, yb = 0.1, yt = 0.262;
    b.lathe([[0.001, y0], [bobaR(y0) - 0.028, y0], [bobaR(yb) - 0.028, yb], [bobaR(yb + 0.012) - 0.008, yb + 0.012], [bobaR(yt) - 0.008, yt], [0.001, yt]], 24, m('#ffffff', { rough: 0.15 }));
  });
}
/** Printed seal film across the top of the cup. */
export function bobaSealMesh() {
  return cached('boba:seal', (b) => {
    const y = BOBA_H + 0.003;
    b.disc(BOBA_R1 + 0.004, m('#fdf6ff', { rough: 0.25 }), { p: [0, y, 0] }, 28);
    b.torus(0.072, 0.006, m('#ff7ab8', { rough: 0.3 }), { p: [0, y + 0.001, 0], s: [1, 0.3, 1] }, 24, 4);
    // a little printed ringed planet and stars
    b.sphere(0.026, m('#7a5cff', { rough: 0.35 }), { p: [0, y + 0.001, 0], s: [1, 0.12, 1] }, 12, 6);
    b.torus(0.04, 0.004, m('#ffd23f', { rough: 0.3 }), { p: [0, y + 0.003, 0], s: [1, 0.3, 0.55] }, 20, 4);
    for (let i = 0; i < 5; i++) b.sphere(0.006, m('#ffd23f', { emit: 0.6 }), { p: [Math.cos(i * 1.26 + 0.3) * 0.056, y + 0.002, Math.sin(i * 1.26 + 0.3) * 0.056], s: [1, 0.3, 1] }, 6, 4);
  });
}
/** Straw stabbed through the seal. */
export function bobaStrawMesh() {
  return cached('boba:straw', (b) => strawTube(b, [0.012, 0.05, 0.008], [0.05, 0.5, 0.03]));
}
/** A straw lying loose (carried, or set on a counter). */
export function looseStrawMesh() {
  return cached('boba:loosestraw', (b) => strawTube(b, [-0.22, 0.02, 0], [0.22, 0.02, 0]));
}

// ---------------------------------------------------------------- stations
const STATION_PREFAB = { stove: 'st_stove', blender: 'st_blender', chop: 'st_chop', grind: 'st_grind', sink: 'st_sink', rack: 'st_rack', carbonator: 'st_carbonator', crate: 'st_crate', extinguisherHook: 'st_ext' };
/** The Zapper: a dark drum with a glowing grate and three tesla coils whose tips arc to each other. */
export const ZAPPER_TIPS = [0, 1, 2].map((i) => {
  const a = (i / 3) * Math.PI * 2 + Math.PI / 2;
  return [Math.cos(a) * 0.33, 0.62, Math.sin(a) * 0.33];
});
export function zapperMesh() {
  return cached('zapper', (b) => {
    const metal = m('#2c2a3c', { rough: 0.35, metal: 0.8 });
    const dark = m('#16141f', { rough: 0.5, metal: 0.4 });
    const copper = m('#e08a4a', { rough: 0.28, metal: 1 });
    const chrome = MAT.chrome;
    const glow = m('#9fe6ff', { rough: 0.2, emit: 3.2 });
    const hazard = m('#ffd23f', { rough: 0.4 });
    // drum with a glowing grate
    b.lathe([[0.001, 0], [0.3, 0], [0.31, 0.02], [0.29, 0.13], [0.26, 0.15], [0.001, 0.15]], 36, metal);
    b.disc(0.24, dark, { p: [0, 0.152, 0] }, 36);
    for (let i = -2; i <= 2; i++) b.box(0.44 - Math.abs(i) * 0.06, 0.012, 0.018, glow, { p: [0, 0.158, i * 0.07] });
    b.torus(0.25, 0.012, glow, { p: [0, 0.152, 0] }, 40, 6);
    for (let i = 0; i < 8; i++) b.box(0.05, 0.03, 0.012, i % 2 ? hazard : dark, { p: [Math.cos((i / 8) * Math.PI * 2) * 0.305, 0.075, Math.sin((i / 8) * Math.PI * 2) * 0.305], r: [0, -(i / 8) * Math.PI * 2 + Math.PI / 2, 0] });
    // three tesla coils
    for (const [x, , z] of ZAPPER_TIPS) {
      b.cylinder(0.055, 0.07, 0.08, metal, { p: [x, 0.04, z] }, 16);
      b.cylinder(0.022, 0.022, 0.42, dark, { p: [x, 0.29, z] }, 10);
      for (let k = 0; k < 9; k++) b.torus(0.03, 0.009, copper, { p: [x, 0.13 + k * 0.034, z] }, 12, 5);
      b.torus(0.05, 0.02, chrome, { p: [x, 0.53, z] }, 18, 8);
      b.sphere(0.034, glow, { p: [x, 0.62, z] }, 12, 8);
    }
  });
}
/** A flat shape (convex, points anticlockwise in x/y) given thickness along z, centred on z = 0. */
function extrude(b, pts, thick, mat, t) {
  const h = thick / 2, P = [], N = [], I = [];
  const n = pts.length;
  for (const [z, nz] of [[h, 1], [-h, -1]]) {
    const base = P.length / 3;
    for (const [x, y] of pts) {
      P.push(x, y, z);
      N.push(0, 0, nz);
    }
    for (let i = 1; i < n - 1; i++) nz > 0 ? I.push(base, base + i, base + i + 1) : I.push(base, base + i + 1, base + i);
  }
  for (let i = 0; i < n; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % n];
    const l = Math.hypot(bx - ax, by - ay) || 1, nx = (by - ay) / l, ny = -(bx - ax) / l;
    const base = P.length / 3;
    P.push(ax, ay, h, ax, ay, -h, bx, by, -h, bx, by, h);
    for (let k = 0; k < 4; k++) N.push(nx, ny, 0);
    I.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return b.pushVerts(P, N, I, mat, t);
}
/**
 * The chef's knife that lives on a chopping board. Blade in the x/y plane, edge along y = 0, the tip at the origin
 * (it rocks on its tip while chopping), handle out along -x.
 */
export function knifeMesh(theme) {
  const acc = theme.accent2;
  return cached('knife:' + acc, (b) => {
    const blade = m('#e9eef6', { rough: 0.24, metal: 0.55 });
    // spine sweeping down to the point; straight edge along the board
    extrude(b, [[-0.25, 0], [0, 0], [-0.03, 0.028], [-0.07, 0.048], [-0.12, 0.058], [-0.25, 0.062]], 0.007, blade);
    // bright honed edge, and a faint glowing line along it
    b.box(0.24, 0.006, 0.0085, MAT.chrome, { p: [-0.13, 0.003, 0] });
    b.box(0.2, 0.004, 0.009, glowM(acc, 1.6), { p: [-0.14, 0.014, 0] });
    // bolster, handle, rivets and a glowing end cap
    b.roundBox(0.016, 0.066, 0.02, 0.006, MAT.chrome, { p: [-0.255, 0.033, 0] });
    b.roundBox(0.13, 0.034, 0.028, 0.012, m('#2c2338', { rough: 0.45 }), { p: [-0.325, 0.033, 0] });
    for (const x of [-0.29, -0.33, -0.37]) for (const z of [-0.0145, 0.0145]) b.cylinder(0.006, 0.006, 0.002, MAT.gold, { p: [x, 0.033, z], r: [Math.PI / 2, 0, 0] }, 8);
    b.sphere(0.012, glowM(acc, 2.2), { p: [-0.392, 0.033, 0] }, 10, 8);
  });
}
export function stationMesh(type, theme) {
  if (type === 'bin') return zapperMesh();
  const a = type !== 'chop' && STATION_PREFAB[type] && modelMesh(STATION_PREFAB[type], 'body');
  if (a) return a;
  const acc = theme.accent, acc2 = theme.accent2;
  return cached('st:' + type + ':' + acc + acc2, (b) => {
    switch (type) {
      case 'stove':
        b.cylinder(0.3, 0.32, 0.03, MAT.black, { p: [0, 0.015, 0] }, 24);
        break;
      case 'blender':
        b.roundBox(0.46, 0.18, 0.46, 0.06, m('#e8e2f2', { rough: 0.3 }), { p: [0, 0.09, 0] });
        b.roundBox(0.3, 0.04, 0.02, 0.01, MAT.black, { p: [0, 0.1, 0.23] });
        b.sphere(0.022, glowM(acc2, 3), { p: [-0.08, 0.1, 0.24] });
        b.sphere(0.022, glowM(acc, 3), { p: [0.0, 0.1, 0.24] });
        b.sphere(0.022, glowM('#7dff9a', 3), { p: [0.08, 0.1, 0.24] });
        break;
      case 'chop': {
        // a chunky butcher-block board: maple with walnut strips, a juice groove, rubber feet, a glowing hang ring
        const maple = m('#dca46a', { rough: 0.5 }), walnut = m('#94562f', { rough: 0.55 }), groove = m('#6a3d20', { rough: 0.7 });
        for (const [x, z] of [[-0.29, -0.19], [0.29, -0.19], [-0.29, 0.19], [0.29, 0.19]]) b.cylinder(0.022, 0.024, 0.012, MAT.rubber, { p: [x, 0.006, z] }, 10);
        b.roundBox(0.7, 0.05, 0.5, 0.02, maple, { p: [0, 0.037, 0] });
        for (const x of [-0.24, -0.12, 0, 0.12, 0.24]) b.box(0.052, 0.004, 0.46, walnut, { p: [x, 0.0615, 0] });
        // the juice groove round the edge
        for (const z of [-0.2, 0.2]) b.box(0.6, 0.004, 0.012, groove, { p: [0, 0.0635, z] });
        for (const x of [-0.305, 0.305]) b.box(0.012, 0.004, 0.412, groove, { p: [x, 0.0635, 0] });
        b.torus(0.024, 0.006, glowM(acc, 2.4), { p: [0.315, 0.064, -0.215] }, 18, 6);
        b.disc(0.02, MAT.black, { p: [0.315, 0.0625, -0.215] }, 14);
        break;
      }
      case 'grind':
        b.lathe([[0.001, 0], [0.16, 0], [0.2, 0.08], [0.22, 0.2], [0.19, 0.2], [0.14, 0.07], [0.001, 0.07]], 20, m('#9a8aa8', { rough: 0.7 }));
        b.torus(0.21, 0.01, glowM(acc, 2), { p: [0, 0.19, 0] }, 24, 4);
        break;
      case 'sink':
        b.roundBox(0.8, 0.02, 0.8, 0.01, MAT.steel, { p: [0, 0.0, 0] });
        b.lathe([[0.001, -0.18], [0.3, -0.18], [0.34, 0.01]], 20, MAT.steel, {});
        b.disc(0.3, m('#5ad0ff', { rough: 0.02, emit: 0.25 }), { p: [0, -0.06, 0] }, 20);
        b.cylinder(0.025, 0.025, 0.4, MAT.chrome, { p: [0, 0.2, -0.36] }, 8);
        b.cylinder(0.02, 0.02, 0.22, MAT.chrome, { p: [0, 0.4, -0.26], r: [Math.PI / 2, 0, 0] }, 8);
        break;
      case 'rack':
        b.box(0.7, 0.02, 0.5, MAT.darkSteel, { p: [0, 0.02, 0] });
        for (const x of [-0.33, 0.33]) b.cylinder(0.015, 0.015, 0.5, MAT.chrome, { p: [x, 0.25, -0.2] }, 6);
        b.box(0.7, 0.02, 0.18, MAT.darkSteel, { p: [0, 0.48, -0.16] });
        b.box(0.66, 0.02, 0.02, glowM(acc2, 2.5), { p: [0, 0.03, 0.25] });
        break;
      case 'bin':
        b.cylinder(0.26, 0.24, 0.1, MAT.darkSteel, { p: [0, 0.05, 0] }, 20);
        b.disc(0.2, MAT.black, { p: [0, 0.101, 0] }, 20);
        b.torus(0.23, 0.02, glowM('#7dff9a', 2.5), { p: [0, 0.1, 0] }, 24, 5);
        b.torus(0.12, 0.012, glowM('#7dff9a', 1.5), { p: [0, 0.102, 0] }, 20, 4);
        break;
      case 'carbonator':
        b.roundBox(0.66, 0.08, 0.62, 0.03, m('#2a3040', { rough: 0.35, metal: 0.6 }), { p: [0, 0.04, 0] });
        b.roundBox(0.66, 0.7, 0.14, 0.04, m('#2a3040', { rough: 0.35, metal: 0.6 }), { p: [0, 0.35, -0.26] });
        b.cylinder(0.03, 0.03, 0.3, MAT.chrome, { p: [0, 0.55, -0.1], r: [Math.PI / 2, 0, 0] }, 8);
        b.cylinder(0.05, 0.03, 0.08, MAT.chrome, { p: [0, 0.5, 0.03] }, 8);
        b.torus(0.17, 0.012, glowM('#7affd8', 3), { p: [0, 0.085, 0.02] }, 24, 4);
        for (let i = 0; i < 3; i++) b.sphere(0.03, glowM(i === 1 ? acc : '#7affd8', 3), { p: [-0.18 + i * 0.18, 0.6, -0.18] });
        break;
      case 'extinguisherHook':
        b.roundBox(0.5, 0.04, 0.5, 0.02, m('#ff5a5a', { rough: 0.5 }), { p: [0, 0.02, 0] });
        b.box(0.44, 0.02, 0.02, glowM('#ff5a5a', 2), { p: [0, 0.045, 0.23] });
        break;
      case 'crate':
        b.roundBox(0.84, 0.16, 0.84, 0.04, m('#3a3448', { rough: 0.4, metal: 0.4 }), { p: [0, 0.08, 0] });
        b.roundBox(0.72, 0.03, 0.72, 0.01, m('#1a1822', { rough: 0.8 }), { p: [0, 0.155, 0] });
        break;
      case 'chute':
        b.cylinder(0.32, 0.36, 0.5, m('#3a3448', { rough: 0.35, metal: 0.6 }), { p: [0, 0.25, 0] }, 20);
        b.torus(0.33, 0.025, glowM(acc, 3), { p: [0, 0.5, 0] }, 24, 5);
        b.disc(0.3, MAT.black, { p: [0, 0.505, 0] }, 20);
        break;
      case 'conveyor':
        b.box(1.0, 0.06, 0.7, MAT.rubber, { p: [0, 0.0, 0] });
        b.box(1.0, 0.08, 0.06, MAT.steel, { p: [0, 0.02, 0.36] });
        b.box(1.0, 0.08, 0.06, MAT.steel, { p: [0, 0.02, -0.36] });
        break;
      case 'arrows':
        for (let i = 0; i < 2; i++) {
          b.box(0.16, 0.005, 0.04, glowM(acc, 2), { p: [-0.25 + i * 0.5, 0.035, 0.07], r: [0, 0.6, 0] });
          b.box(0.16, 0.005, 0.04, glowM(acc, 2), { p: [-0.25 + i * 0.5, 0.035, -0.07], r: [0, -0.6, 0] });
        }
        break;
      case 'cups': {
        // two stacks of nested boba cups, upside down
        b.roundBox(0.66, 0.05, 0.5, 0.02, m('#2a3040', { rough: 0.35, metal: 0.6 }), { p: [0, 0.025, 0] });
        b.box(0.6, 0.02, 0.02, glowM(acc2, 2.5), { p: [0, 0.04, 0.25] });
        const frost = m('#eef6ff', { rough: 0.22 }), lip = m('#ffffff', { rough: 0.2 });
        for (const x of [-0.16, 0.16]) {
          const y0 = 0.05, h = 0.44;
          b.lathe([[0.001, y0], [BOBA_R1, y0], [BOBA_R0, y0 + h], [0.001, y0 + h]], 20, frost, { p: [x, 0, 0] });
          for (let i = 0; i < 12; i++) {
            const y = y0 + 0.004 + i * 0.035;
            b.torus(BOBA_R1 - ((BOBA_R1 - BOBA_R0) * (y - y0)) / h + 0.003, 0.004, lip, { p: [x, y, 0] }, 20, 4);
          }
        }
        break;
      }
      case 'sealer': {
        const dark = m('#2a3040', { rough: 0.35, metal: 0.6 });
        const shell = m('#f2eaf8', { rough: 0.3 });
        b.roundBox(0.64, 0.06, 0.6, 0.03, dark, { p: [0, 0.03, 0] });
        b.cylinder(0.13, 0.13, 0.012, MAT.black, { p: [0, 0.062, 0.02] }, 24);
        b.torus(0.14, 0.01, glowM(acc, 3), { p: [0, 0.066, 0.02] }, 28, 4);
        b.roundBox(0.6, 0.78, 0.16, 0.05, shell, { p: [0, 0.39, -0.24] });
        // film roll on top
        b.cylinder(0.07, 0.07, 0.5, m('#ff9ad0', { rough: 0.4 }), { p: [0, 0.84, -0.22], r: [0, 0, Math.PI / 2] }, 16);
        b.cylinder(0.025, 0.025, 0.56, MAT.chrome, { p: [0, 0.84, -0.22], r: [0, 0, Math.PI / 2] }, 8);
        for (let i = 0; i < 3; i++) b.sphere(0.022, glowM(i === 1 ? acc : acc2, 3), { p: [-0.14 + i * 0.14, 0.64, -0.155] }, 8, 6);
        // guide rails for the press head
        for (const x of [-0.2, 0.2]) b.cylinder(0.018, 0.018, 0.6, MAT.chrome, { p: [x, 0.36, -0.12] }, 8);
        break;
      }
      case 'sealerHead':
        b.roundBox(0.46, 0.1, 0.3, 0.035, m('#e7dcf2', { rough: 0.3 }), { p: [0, 0, 0] });
        b.cylinder(0.125, 0.125, 0.02, MAT.chrome, { p: [0, -0.06, 0] }, 24);
        b.torus(0.12, 0.008, glowM(acc, 2.5), { p: [0, -0.07, 0] }, 24, 4);
        break;
      case 'straws':
        b.roundBox(0.5, 0.04, 0.5, 0.02, m('#2a3040', { rough: 0.35, metal: 0.6 }), { p: [0, 0.02, 0] });
        b.lathe([[0.001, 0.04], [0.11, 0.04], [0.13, 0.3], [0.12, 0.3], [0.1, 0.06], [0.001, 0.06]], 20, m('#fff0f8', { rough: 0.25 }));
        b.torus(0.123, 0.012, glowM(acc, 2), { p: [0, 0.2, 0] }, 24, 4);
        for (let i = 0; i < 13; i++) {
          const a = i * 2.4, r = 0.02 + (i % 3) * 0.028, lean = 0.06 + (i % 3) * 0.05;
          strawTube(b, [Math.cos(a) * r, 0.08, Math.sin(a) * r], [Math.cos(a) * (r + lean), 0.5 + (i % 4) * 0.03, Math.sin(a) * (r + lean)]);
        }
        break;
      case 'teleport':
        b.cylinder(0.44, 0.46, 0.05, m('#2a2a3a', { rough: 0.3, metal: 0.7 }), { p: [0, 0.025, 0] }, 28);
        b.torus(0.4, 0.025, glowM(acc2, 4), { p: [0, 0.05, 0] }, 28, 5);
        b.torus(0.25, 0.015, glowM(acc, 3), { p: [0, 0.052, 0] }, 24, 4);
        b.disc(0.2, glowM(acc2, 1.2), { p: [0, 0.052, 0] }, 20);
        break;
    }
  });
}
export function selectorMesh() {
  return cached('selector', (b) => {
    const w = m('#ffffff', { emit: 1 });
    const L = 0.98, T = 0.07, H = 0.03;
    // corner brackets
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) {
        b.box(0.3, H, T, w, { p: [sx * (L / 2 - 0.15), 0, sz * (L / 2 - T / 2)] });
        b.box(T, H, 0.3, w, { p: [sx * (L / 2 - T / 2), 0, sz * (L / 2 - 0.15)] });
      }
    // thin full outline
    b.box(L, H * 0.6, 0.015, w, { p: [0, 0, L / 2 - 0.01] });
    b.box(L, H * 0.6, 0.015, w, { p: [0, 0, -L / 2 + 0.01] });
    b.box(0.015, H * 0.6, L, w, { p: [L / 2 - 0.01, 0, 0] });
    b.box(0.015, H * 0.6, L, w, { p: [-L / 2 + 0.01, 0, 0] });
  });
}
export function crateBandMesh(color) {
  return cached('crateband' + color, (b) => {
    b.box(0.86, 0.035, 0.02, glowM(color, 2.2), { p: [0, 0.12, 0.43] });
    b.box(0.02, 0.035, 0.86, glowM(color, 1.2), { p: [0.43, 0.12, 0] });
    b.box(0.02, 0.035, 0.86, glowM(color, 1.2), { p: [-0.43, 0.12, 0] });
  });
}
export function stoveRingMesh() {
  return cached('stovering', (b) => {
    b.torus(0.22, 0.02, m('#ffffff', { rough: 0.3, emit: 1 }), { p: [0, 0.035, 0] }, 28, 4);
    b.torus(0.13, 0.015, m('#ffffff', { rough: 0.3, emit: 1 }), { p: [0, 0.035, 0] }, 24, 4);
  });
}
export function pestleMesh() {
  const a = modelMesh('st_grind', 'pestle');
  if (a) return a;
  return cached('pestle', (b) => b.capsule(0.04, 0.26, m('#b8aac8', { rough: 0.6 }), { p: [0, 0.2, 0] }));
}
export function beamMesh(r = 0.4, h = 2.5) {
  return cached('beam' + r + h, (b) => b.lathe([[r, -h / 2], [r * 0.6, h / 2]], 24, m('#ffffff'), {}));
}
export function coneBeamMesh(r0, r1, h) {
  return cached('cbeam' + r0 + r1 + h, (b) => b.lathe([[r1, -h / 2], [r0, h / 2]], 24, m('#ffffff'), {}));
}
export function sphereMesh(color = '#ffffff', emit = 0) {
  return cached('sph' + color + emit, (b) => b.sphere(1, m(color, { rough: 0.3, emit }), {}, 16, 12));
}
export function fireMesh() {
  return cached('fire', (b) => {
    b.cone(0.22, 0.6, m('#ff7a2a', { emit: 1 }), { p: [0, 0.3, 0] }, 10);
    b.cone(0.14, 0.5, m('#ffd35a', { emit: 1 }), { p: [0.1, 0.25, 0.05] }, 8);
    b.cone(0.12, 0.45, m('#ff4a2a', { emit: 1 }), { p: [-0.1, 0.22, -0.04] }, 8);
  });
}
export function ringMesh() {
  return cached('ringfx', (b) => b.torus(0.5, 0.03, m('#ffffff'), {}, 32, 4));
}
export function discFxMesh() {
  return cached('discfx', (b) => b.disc(0.5, m('#ffffff'), {}, 24));
}

// ---------------------------------------------------------------- chef
export function buildChef(colorHex) {
  const c = colorHex;
  const root = new Node();
  const body = new Node();
  root.add(body);
  const asset = modelParts('chef', c);
  if (asset) return buildChefFromAsset(c, root, body, asset);
  const bodyMesh = cached('chefbody' + c, (b) => {
    const suit = m(c, { rough: 0.45 });
    const suitDark = m(shade(c, 0.6), { rough: 0.5 });
    b.lathe([[0.001, 0.12], [0.24, 0.14], [0.3, 0.3], [0.29, 0.5], [0.22, 0.66], [0.001, 0.7]], 20, suit, {});
    // apron
    b.lathe([[0.302, 0.18], [0.305, 0.48]], 20, m('#fff6ee', { rough: 0.6 }), { p: [0, 0, 0.0], s: [1.0, 1, 1.0] });
    b.torus(0.3, 0.018, MAT.gold, { p: [0, 0.48, 0] }, 24, 5);
    // belt with glow
    b.torus(0.305, 0.02, m(c, { emit: 2.5 }), { p: [0, 0.2, 0] }, 24, 5);
    // backpack oxygen tank
    b.capsule(0.08, 0.18, MAT.steel, { p: [0, 0.42, -0.28] });
    b.sphere(0.03, m(c, { emit: 3 }), { p: [0, 0.56, -0.3] });
    // feet
    b.capsule(0.07, 0.08, suitDark, { p: [-0.12, 0.07, 0.04], r: [Math.PI / 2, 0, 0] });
    b.capsule(0.07, 0.08, suitDark, { p: [0.12, 0.07, 0.04], r: [Math.PI / 2, 0, 0] });
  });
  body.add(N(bodyMesh));
  // head inside bubble
  const head = new Node();
  head.position = [0, 0.86, 0];
  body.add(head);
  head.add(
    N(cached('chefhead' + c, (b) => {
      b.sphere(0.2, m('#bff5ea', { rough: 0.5 }), { s: [1, 0.95, 1] });
      // eyes
      b.sphere(0.045, MAT.black, { p: [-0.075, 0.03, 0.17], s: [1, 1.3, 0.6] });
      b.sphere(0.045, MAT.black, { p: [0.075, 0.03, 0.17], s: [1, 1.3, 0.6] });
      b.sphere(0.014, m('#ffffff', { emit: 2 }), { p: [-0.065, 0.055, 0.2] });
      b.sphere(0.014, m('#ffffff', { emit: 2 }), { p: [0.085, 0.055, 0.2] });
      // cheeks
      b.sphere(0.03, m('#ff9ab8'), { p: [-0.13, -0.04, 0.13], s: [1, 0.6, 0.5] });
      b.sphere(0.03, m('#ff9ab8'), { p: [0.13, -0.04, 0.13], s: [1, 0.6, 0.5] });
      // antenna
      b.cylinder(0.01, 0.012, 0.22, MAT.darkSteel, { p: [0, 0.29, 0] }, 6);
      // helmet collar
      b.torus(0.25, 0.035, MAT.chrome, { p: [0, -0.2, 0] }, 24, 6);
    }))
  );
  const bulb = N(sphereMesh(c, 5));
  bulb.setScale(0.05);
  bulb.position = [0, 0.42, 0];
  head.add(bulb);
  const helmet = new Node(cached('helmet', (b) => b.sphere(0.3, m('#bfe8ff'), {}, 20, 14)), { fx: { mode: 1 }, castShadow: false });
  helmet.tint = [0.7, 0.9, 1.1, 0.9];
  helmet.position = [0, 0.02, 0];
  head.add(helmet);
  // arms
  const armMesh = cached('chefarm' + c, (b) => {
    b.capsule(0.055, 0.16, m(c, { rough: 0.45 }), { p: [0, -0.08, 0] });
    b.sphere(0.07, m('#fff6ee', { rough: 0.6 }), { p: [0, -0.2, 0] });
  });
  const armL = N(armMesh), armR = N(armMesh);
  armL.position = [-0.3, 0.52, 0.02];
  armR.position = [0.3, 0.52, 0.02];
  body.add(armL, armR);
  const hand = new Node();
  hand.position = [0, 0.5, 0.42];
  body.add(hand);
  return { root, body, head, armL, armR, hand, bulb, helmet };
}

function buildChefFromAsset(c, root, body, parts) {
  const get = (n) => parts.find((p) => p.name.startsWith(n));
  body.add(N(get('body').mesh));
  const hp = get('head');
  const head = new Node();
  head.position = hp.pos.slice();
  body.add(head);
  head.add(N(hp.mesh));
  const bulb = N(sphereMesh(c, 5));
  bulb.setScale(0.05);
  bulb.position = [0, 0.42, 0];
  head.add(bulb);
  const helmet = new Node(cached('helmet', (b) => b.sphere(0.3, m('#bfe8ff'), {}, 32, 20)), { fx: { mode: 1 }, castShadow: false });
  helmet.tint = [0.7, 0.9, 1.1, 0.9];
  helmet.position = [0, 0.02, 0];
  head.add(helmet);
  const ap = get('arm');
  const armL = N(ap.mesh), armR = N(ap.mesh);
  armL.position = [-0.3, ap.pos[1], 0.02];
  armR.position = [0.3, ap.pos[1], 0.02];
  body.add(armL, armR);
  const hand = new Node();
  hand.position = [0, 0.5, 0.42];
  body.add(hand);
  return { root, body, head, armL, armR, hand, bulb, helmet };
}

function shade(hex, k) {
  const c = parseInt(hex.slice(1), 16);
  const r = Math.round(((c >> 16) & 255) * k), g = Math.round(((c >> 8) & 255) * k), bb = Math.round((c & 255) * k);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + bb).toString(16).slice(1);
}

// ---------------------------------------------------------------- alien guests
export function buildAlien(speciesId) {
  const sp = SPECIES.find((s) => s.id === speciesId) || SPECIES[0];
  const root = new Node();
  const body = new Node();
  root.add(body);
  const asset = modelParts('alien_' + sp.id);
  if (asset) {
    body.add(N(asset.find((p) => p.name === 'body').mesh));
    const face = new Node();
    body.add(face);
    const eyes = [];
    let mouth = null;
    for (const p of asset) {
      if (p.name.startsWith('eye')) {
        const e = N(p.mesh);
        e.position = p.pos.slice();
        face.add(e);
        eyes.push(e);
      } else if (p.name.startsWith('mouth')) {
        mouth = N(p.mesh);
        mouth.position = p.pos.slice();
        face.add(mouth);
      }
    }
    if (!mouth) mouth = new Node();
    return { root, body, eyes, mouth, face, species: sp, asset: true };
  }
  const c1 = m(sp.color, { rough: 0.35 });
  const c2 = m(sp.color2, { rough: 0.45 });
  const eyeW = m('#ffffff', { rough: 0.2 });
  const glowE = m(sp.color, { emit: 3 });
  let eyes = [];
  const face = new Node();
  const mesh = cached('alien:' + sp.id, (b) => {
    switch (sp.id) {
      case 'glorb':
        b.lathe([[0.001, 0], [0.34, 0.02], [0.36, 0.2], [0.28, 0.5], [0.15, 0.66], [0.001, 0.7]], 22, m(sp.color, { rough: 0.15, emit: 0.15 }), {});
        b.sphere(0.06, m(sp.color2, { emit: 1 }), { p: [0.12, 0.3, 0.2] });
        b.sphere(0.04, m(sp.color2, { emit: 1 }), { p: [-0.15, 0.2, 0.22] });
        break;
      case 'zeeb':
        b.capsule(0.2, 0.55, c1, { p: [0, 0.48, 0] });
        b.cylinder(0.012, 0.012, 0.3, c2, { p: [-0.1, 1.0, 0], r: [0, 0, 0.35] }, 6);
        b.cylinder(0.012, 0.012, 0.3, c2, { p: [0.1, 1.0, 0], r: [0, 0, -0.35] }, 6);
        b.sphere(0.045, glowE, { p: [-0.155, 1.14, 0] });
        b.sphere(0.045, glowE, { p: [0.155, 1.14, 0] });
        b.lathe([[0.001, 0.05], [0.26, 0.08], [0.24, 0.28], [0.001, 0.3]], 18, c2, {});
        break;
      case 'nuvari':
        b.hemisphere(0.34, m(sp.color, { rough: 0.2, emit: 0.35 }), { p: [0, 0.55, 0], s: [1, 1.1, 1] });
        b.disc(0.34, m(sp.color2, { emit: 0.5 }), { p: [0, 0.55, 0], s: [1, -1, 1] });
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          b.capsule(0.035, 0.38, m(i % 2 ? sp.color : sp.color2, { emit: 0.6 }), { p: [Math.cos(a) * 0.22, 0.32, Math.sin(a) * 0.22], r: [Math.sin(a) * 0.2, 0, -Math.cos(a) * 0.2] });
        }
        break;
      case 'krix':
        b.roundBox(0.6, 0.36, 0.46, 0.12, c1, { p: [0, 0.34, 0] });
        b.roundBox(0.44, 0.08, 0.36, 0.03, MAT.darkSteel, { p: [0, 0.14, 0] });
        for (const s of [-1, 1]) {
          b.capsule(0.05, 0.22, c2, { p: [s * 0.36, 0.36, 0.1], r: [0, 0, s * 0.8] });
          b.sphere(0.1, c1, { p: [s * 0.46, 0.5, 0.18], s: [1, 0.7, 1] });
          b.box(0.1, 0.03, 0.12, c2, { p: [s * 0.46, 0.55, 0.26], r: [0.3, 0, 0] });
          b.cylinder(0.02, 0.02, 0.2, MAT.darkSteel, { p: [s * 0.1, 0.62, 0.05] }, 6);
        }
        b.box(0.44, 0.04, 0.02, m('#fff', { emit: 2 }), { p: [0, 0.3, 0.235] });
        break;
      case 'moff':
        b.capsule(0.2, 0.3, m(sp.color, { rough: 0.9 }), { p: [0, 0.42, 0] });
        b.sphere(0.2, m('#ffffff', { rough: 1 }), { p: [0, 0.5, 0.06], s: [1.1, 0.7, 1] });
        for (const s of [-1, 1]) {
          b.sphere(0.32, m(sp.color2, { rough: 0.8, emit: 0.08 }), { p: [s * 0.3, 0.55, -0.15], s: [0.9, 1.1, 0.08], r: [0, s * 0.5, s * 0.3] });
          b.sphere(0.08, m('#ff9ad8', { emit: 1.4 }), { p: [s * 0.36, 0.64, -0.13], s: [1, 1, 0.3], r: [0, s * 0.5, 0] });
          b.leaf(0.2, 0.05, m(sp.color2), { p: [s * 0.08, 0.88, 0.08], r: [-0.8, s * 0.4, 0] });
        }
        break;
    }
  });
  body.add(N(mesh));
  // eyes node group (animated blink)
  const eyeMesh = cached('eye', (b) => {
    b.sphere(0.07, eyeW, {}, 12, 10);
    b.sphere(0.035, MAT.black, { p: [0, 0, 0.05] }, 10, 8);
    b.sphere(0.012, m('#ffffff', { emit: 2 }), { p: [0.012, 0.015, 0.075] }, 6, 4);
  });
  const addEye = (x, y, z, s = 1) => {
    const e = N(eyeMesh);
    e.position = [x, y, z];
    e.setScale(s);
    face.add(e);
    eyes.push(e);
  };
  switch (sp.id) {
    case 'glorb': addEye(0, 0.42, 0.26, 1.8); break;
    case 'zeeb': addEye(-0.09, 0.78, 0.16); addEye(0.09, 0.78, 0.16); addEye(0, 0.9, 0.15, 0.8); break;
    case 'nuvari': addEye(-0.1, 0.68, 0.28, 0.9); addEye(0.1, 0.68, 0.28, 0.9); break;
    case 'krix': addEye(-0.1, 0.76, 0.1, 0.8); addEye(0.1, 0.76, 0.1, 0.8); break;
    case 'moff': addEye(-0.1, 0.62, 0.16, 1.1); addEye(0.1, 0.62, 0.16, 1.1); break;
  }
  // mouth
  const mouth = N(cached('mouth', (b) => b.torus(0.05, 0.012, MAT.black, { r: [Math.PI / 2, 0, 0] }, 12, 4, Math.PI)));
  const mouthPos = { glorb: [0, 0.24, 0.33], zeeb: [0, 0.64, 0.19], nuvari: [0, 0.58, 0.32], krix: [0, 0.3, 0.25], moff: [0, 0.5, 0.26] }[sp.id];
  mouth.position = mouthPos;
  mouth.rotation = [0, 0, Math.PI];
  face.add(mouth);
  body.add(face);
  return { root, body, eyes, mouth, face, species: sp };
}

// ---------------------------------------------------------------- lounge decor
export function stoolMesh(theme) {
  return cached('stool' + theme.accent, (b) => {
    b.cylinder(0.05, 0.05, 0.45, MAT.chrome, { p: [0, 0.23, 0] }, 8);
    b.cylinder(0.2, 0.22, 0.03, MAT.chrome, { p: [0, 0.015, 0] }, 16);
    b.cylinder(0.22, 0.2, 0.1, m(theme.accent2, { rough: 0.6 }), { p: [0, 0.5, 0] }, 18);
    b.torus(0.21, 0.012, glowM(theme.accent2, 1.5), { p: [0, 0.45, 0] }, 20, 4);
  });
}
export function plantMesh(theme) {
  return cached('plant' + theme.accent2, (b) => {
    b.lathe([[0.001, 0], [0.2, 0], [0.26, 0.4], [0.24, 0.42], [0.001, 0.38]], 18, m('#2a2438', { rough: 0.4, metal: 0.3 }));
    b.torus(0.255, 0.012, glowM(theme.accent, 2), { p: [0, 0.4, 0] }, 20, 4);
    for (let i = 0; i < 9; i++) {
      const a = i * 0.7;
      b.leaf(0.28, 0.07, m(i % 2 ? '#2a8a6a' : '#3ab07a', { rough: 0.5 }), { p: [Math.cos(a) * 0.1, 0.62 + (i % 3) * 0.08, Math.sin(a) * 0.1], r: [-0.9 + (i % 3) * 0.2, a, 0] });
    }
    for (let i = 0; i < 4; i++) b.sphere(0.04, glowM(i % 2 ? theme.accent : theme.accent2, 4), { p: [Math.cos(i * 1.6) * 0.18, 0.85 + (i % 2) * 0.15, Math.sin(i * 1.6) * 0.18] });
  });
}
export function lampMesh(theme) {
  return cached('lamp' + theme.accent, (b) => {
    b.cylinder(0.008, 0.008, 1.6, MAT.black, { p: [0, 0.8, 0] }, 4);
    b.lathe([[0.001, -0.02], [0.26, -0.02], [0.2, 0.14], [0.06, 0.2], [0.001, 0.2]], 20, m('#2a2438', { rough: 0.3, metal: 0.8 }));
    b.disc(0.24, m('#fff4dc', { emit: 6 }), { p: [0, -0.025, 0], s: [1, -1, 1] }, 20);
    b.torus(0.26, 0.012, MAT.gold, { p: [0, -0.02, 0] }, 24, 4);
  });
}
export function tableMesh(theme) {
  return cached('table' + theme.accent, (b) => {
    b.cylinder(0.3, 0.3, 0.04, m('#f5efe6', { rough: 0.2 }), { p: [0, 0.72, 0] }, 24);
    b.torus(0.3, 0.015, MAT.gold, { p: [0, 0.72, 0] }, 24, 4);
    b.cylinder(0.04, 0.04, 0.7, MAT.chrome, { p: [0, 0.36, 0] }, 8);
    b.cylinder(0.18, 0.2, 0.03, MAT.chrome, { p: [0, 0.015, 0] }, 16);
  });
}
export function teapotMesh() {
  return cached('teapot', (b) => {
    b.lathe([[0.001, 0], [0.1, 0], [0.14, 0.06], [0.13, 0.13], [0.07, 0.18], [0.001, 0.19]], 16, MAT.ceramic);
    b.sphere(0.02, MAT.gold, { p: [0, 0.2, 0] });
    b.cylinder(0.012, 0.02, 0.12, MAT.ceramic, { p: [0.15, 0.1, 0], r: [0, 0, -0.9] }, 6);
    b.torus(0.05, 0.012, MAT.ceramic, { p: [-0.14, 0.1, 0], r: [Math.PI / 2, 0, 0] }, 10, 4, Math.PI * 2);
  });
}

export { col };
